import React, { useState, useEffect } from 'react';
import './CreateHealthWorkerModal.css';
import { createHealthWorker, getFacilities, getJobTitles, getLgasByState, getStates } from '../api';
import logger from '../logger';

const CreateHealthWorkerModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    gender: 'female',
    cadre: '',
    dob: '',
    highestQualification: '',
    stateId: '',
    lgaId: '',
    facilityId: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Location dropdown data
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loadingLocationData, setLoadingLocationData] = useState(false);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [jobTitles, setJobTitles] = useState([]);
  const [loadingJobTitles, setLoadingJobTitles] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const fetchJobTitles = async () => {
      setLoadingJobTitles(true);

      try {
        const data = await getJobTitles();
        if (isCancelled) return;

        const normalizedJobTitles = data
          .map((jobTitle) => ({
            id: jobTitle.id || jobTitle.job_title_id || jobTitle.jobTitleId,
            name:
              jobTitle.name ||
              jobTitle.title ||
              jobTitle.job_title_name ||
              jobTitle.jobTitleName ||
              jobTitle.label ||
              'Unknown Job Title'
          }))
          .filter((jobTitle) => jobTitle.id !== undefined && jobTitle.id !== null && jobTitle.name)
          .sort((a, b) => a.name.localeCompare(b.name));

        setJobTitles(normalizedJobTitles);
      } catch (err) {
        if (!isCancelled) {
          logger.error('Failed to fetch job titles:', err);
          setJobTitles([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingJobTitles(false);
        }
      }
    };

    fetchJobTitles();

    return () => {
      isCancelled = true;
    };
  }, [isOpen]);

  // Fetch states directly — only once the modal is actually opened, not on
  // mount (this component stays mounted in the background even when closed).
  // Facilities are fetched separately, scoped to the selected LGA, once the
  // user actually picks one (see the effect below) — we never load the full
  // facilities table just to build this dropdown.
  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const fetchStates = async () => {
      setLoadingLocationData(true);
      try {
        const data = await getStates();
        if (isCancelled) return;

        const normalizedStates = (Array.isArray(data) ? data : [])
          .map((state) => ({
            id: state.id,
            name: state.name || state.state_name || 'Unknown State',
          }))
          .filter((state) => state.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));

        setStates(normalizedStates);
      } catch (err) {
        if (!isCancelled) {
          logger.error('Failed to fetch states:', err);
        }
      } finally {
        if (!isCancelled) {
          setLoadingLocationData(false);
        }
      }
    };
    fetchStates();

    return () => {
      isCancelled = true;
    };
  }, [isOpen]);

  // Fetch LGAs from the API when state changes
  useEffect(() => {
    let isCancelled = false;

    setLgas([]);
    setFacilities([]);
    setFormData(prev => ({ ...prev, lgaId: '', facilityId: '' }));

    if (!formData.stateId) {
      return;
    }

    const fetchLgas = async () => {
      setLoadingLgas(true);

      try {
        const data = await getLgasByState(formData.stateId);
        if (isCancelled) return;

        const normalizedLgas = data
          .map((lga) => ({
            id: lga.id || lga.lga_id || lga.lgaId,
            name: lga.name || lga.lga_name || lga.lgaName || 'Unknown LGA'
          }))
          .filter((lga) => lga.id !== undefined && lga.id !== null)
          .sort((a, b) => a.name.localeCompare(b.name));

        setLgas(normalizedLgas);
      } catch (err) {
        if (!isCancelled) {
          logger.error('Failed to fetch LGAs for selected state:', err);
          setLgas([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingLgas(false);
        }
      }
    };

    fetchLgas();

    return () => {
      isCancelled = true;
    };
  }, [formData.stateId]);

  // Fetch facilities when LGA changes
  useEffect(() => {
    let isCancelled = false;

    const fetchFacilitiesByLga = async () => {
      if (!formData.lgaId) {
        setFacilities([]);
        setLoadingFacilities(false);
        return;
      }

      setFacilities([]);
      setFormData(prev => ({ ...prev, facilityId: '' }));
      setLoadingFacilities(true);

      try {
        const lgaFacilities = await getFacilities(formData.lgaId);
        if (isCancelled) return;

        const sortedFacilities = [...lgaFacilities].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setFacilities(sortedFacilities);
      } catch (err) {
        if (!isCancelled) {
          logger.error('Failed to fetch facilities for selected LGA:', err);
          setFacilities([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingFacilities(false);
        }
      }
    };

    fetchFacilitiesByLga();

    return () => {
      isCancelled = true;
    };
  }, [formData.lgaId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        gender: 'female',
        cadre: '',
        dob: '',
        highestQualification: '',
        stateId: '',
        lgaId: '',
        facilityId: '',
      });
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.firstName.trim()) {
      setError('First name is required');
      setLoading(false);
      return;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      setLoading(false);
      return;
    }
    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      setLoading(false);
      return;
    }
    if (!/^\d{11}$/.test(formData.phoneNumber.trim())) {
      setError('Phone number must be exactly 11 digits');
      setLoading(false);
      return;
    }
    if (!formData.gender) {
      setError('Gender is required');
      setLoading(false);
      return;
    }
    if (!formData.cadre) {
      setError('Cadre (Job Title) is required');
      setLoading(false);
      return;
    }
    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        firstname: formData.firstName.trim(),
        lastname: formData.lastName.trim(),
        othernames: formData.middleName.trim() || null,
        phone_number: formData.phoneNumber.trim(),
        gender: formData.gender,
        job_title_id: parseInt(formData.cadre, 10),
        email: formData.email.trim() || null,
        facility_id: formData.facilityId ? parseInt(formData.facilityId, 10) : null,
        lga_id: formData.lgaId ? parseInt(formData.lgaId, 10) : null,
        lgaId: formData.lgaId ? parseInt(formData.lgaId, 10) : null,
        state_id: formData.stateId ? parseInt(formData.stateId, 10) : null,
        dob: formData.dob || null,
        highest_qualification: formData.highestQualification || null,
        is_admin: 0,
        is_active: 1,
      };

      const result = await createHealthWorker(payload);
      onSubmit(result);
      onClose();
      // Reset form
      setFormData({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        gender: 'female',
        cadre: '',
        dob: '',
        highestQualification: '',
        stateId: '',
        lgaId: '',
        facilityId: '',
      });
    } catch (err) {
      const errorMessage = err.message || 'Failed to create health worker. Please try again.';
      setError(errorMessage);
      logger.error("Failed to create health worker", err);
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-health-worker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Health Worker</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 
                       5 6.41 10.59 12 5 17.59 
                       6.41 19 12 13.41 17.59 19 
                       19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First name <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label htmlFor="middleName">Middle name</label>
              <input
                type="text"
                id="middleName"
                value={formData.middleName}
                onChange={(e) => handleInputChange('middleName', e.target.value)}
                placeholder="Optional"
                maxLength={255}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="lastName">Last name <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone number <span style={{ color: 'red' }}>*</span></label>
              <input
                type="tel"
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                  handleInputChange('phoneNumber', digits);
                }}
                required
                maxLength={11}
                placeholder="e.g., 08012345678"
              />
            </div>

            <div className="form-group">
              <label htmlFor="gender">Gender <span style={{ color: 'red' }}>*</span></label>
              <select
                id="gender"
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                required
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dob">Date of Birth</label>
              <input
                type="date"
                id="dob"
                value={formData.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label htmlFor="highestQualification">Highest Qualification</label>
              <select
                id="highestQualification"
                value={formData.highestQualification}
                onChange={(e) => handleInputChange('highestQualification', e.target.value)}
              >
                <option value="">Select qualification</option>
                <option value="FSLC">FSLC (First School Leaving Certificate)</option>
                <option value="JSCE">JSCE (Junior Secondary Certificate)</option>
                <option value="SSCE">SSCE / WAEC / NECO</option>
                <option value="OND">OND (Ordinary National Diploma)</option>
                <option value="HND">HND (Higher National Diploma)</option>
                <option value="NCE">NCE (Nigeria Certificate in Education)</option>
                <option value="BSc">B.Sc / B.A / B.Ed (Bachelor's Degree)</option>
                <option value="MBBS">MBBS / BDS</option>
                <option value="PGD">PGD (Postgraduate Diploma)</option>
                <option value="MSc">M.Sc / M.A / MBA (Master's Degree)</option>
                <option value="PhD">Ph.D (Doctorate)</option>
                <option value="Fellowship">Fellowship (e.g. FMCP, FWACS)</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cadre">Cadre (Job Title) <span style={{ color: 'red' }}>*</span></label>
              <select
                id="cadre"
                value={formData.cadre}
                onChange={(e) => handleInputChange('cadre', e.target.value)}
                required
                disabled={loadingJobTitles}
              >
                <option value="">{loadingJobTitles ? 'Loading job titles...' : 'Select cadre'}</option>
                {jobTitles.map(jobTitle => (
                  <option key={jobTitle.id} value={jobTitle.id}>{jobTitle.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="state">State</label>
              <select
                id="state"
                value={formData.stateId}
                onChange={(e) => handleInputChange('stateId', e.target.value)}
                disabled={loadingLocationData}
              >
                <option value="">{loadingLocationData ? 'Loading data...' : 'Select state'}</option>
                {states.map(state => (
                  <option key={state.id} value={state.id}>{state.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="lga">Local Government Area (LGA)</label>
              <select
                id="lga"
                value={formData.lgaId}
                onChange={(e) => handleInputChange('lgaId', e.target.value)}
                disabled={!formData.stateId || loadingLgas}
              >
                <option value="">
                  {!formData.stateId
                    ? 'Select state first'
                    : loadingLgas
                    ? 'Loading LGAs...'
                    : 'Select LGA'}
                </option>
                {lgas.map(lga => (
                  <option key={lga.id} value={lga.id}>{lga.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="facility">Facility</label>
              <select
                id="facility"
                value={formData.facilityId}
                onChange={(e) => handleInputChange('facilityId', e.target.value)}
                disabled={!formData.lgaId || loadingFacilities}
              >
                <option value="">
                  {!formData.lgaId
                    ? 'Select LGA first'
                    : loadingFacilities
                    ? 'Loading facilities...'
                    : 'Select facility'}
                </option>
                {facilities.map(facility => (
                  <option key={facility.id} value={facility.id}>{facility.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-create-account" disabled={loading}>
              {loading ? 'Creating...' : 'Create account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateHealthWorkerModal;
